import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NbComponentStatus, NbDialogService, NbGlobalPhysicalPosition, NbToastrService } from '@nebular/theme';
import { environment } from 'src/environments/environment';
import { SharedService } from './shared.service';
import { XeroAuthService } from './xero-auth.service';
import { XeroReconnectDialogComponent } from 'src/app/home/xero/xero-reconnect-dialog/xero-reconnect-dialog.component';

/**
 * Simple options for API calls
 */
interface XeroApiOptions {
  showSuccessToast?: boolean;
  successTitle?: string;
  successMessage?: string;
  actionDescription?: string;
  /** Background call the user didn't initiate — log failures, never toast or prompt */
  silent?: boolean;
}

/**
 * Streamlined helper for Xero API calls with consistent error handling
 */
@Injectable({
  providedIn: 'root',
})
export class XeroApiService {
  private readonly FUNCTIONS_BASE_URL = environment.functionsRootUrl;

  constructor(
    private http: HttpClient,
    private ss: SharedService,
    private toastrService: NbToastrService,
    private xeroAuthService: XeroAuthService,
    private dialogService: NbDialogService,
  ) {}

  /**
   * Make a GET request to a Xero API endpoint
   */
  async get<T>(endpoint: string, options?: XeroApiOptions, params?: Record<string, any>): Promise<T | null> {
    return this.request<T>('GET', endpoint, options, params);
  }

  /**
   * Make a POST request to a Xero API endpoint
   */
  async post<T>(endpoint: string, body: any, options?: XeroApiOptions, params?: Record<string, any>): Promise<T | null> {
    return this.request<T>('POST', endpoint, options, params, body);
  }

  /**
   * Make a PUT request to a Xero API endpoint
   */
  async put<T>(endpoint: string, body: any, options?: XeroApiOptions, params?: Record<string, any>): Promise<T | null> {
    return this.request<T>('PUT', endpoint, options, params, body);
  }

  /**
   * Make a DELETE request to a Xero API endpoint
   */
  async delete<T>(endpoint: string, options?: XeroApiOptions, params?: Record<string, any>): Promise<T | null> {
    return this.request<T>('DELETE', endpoint, options, params);
  }

  /**
   * Core request method handling all HTTP methods
   */
  private async request<T>(
    method: string,
    endpoint: string,
    options: XeroApiOptions = {},
    queryParams: Record<string, any> = {},
    body?: any,
  ): Promise<T | null> {
    try {
      // Get current user
      const user = await this.ss.getUser();
      const url = `${this.FUNCTIONS_BASE_URL}/${endpoint}`;
      const params = { userUid: user.uid, ...queryParams };

      // Execute request based on method
      let response: T;

      if (method === 'GET') {
        response = await this.http.get<T>(url, { params }).toPromise();
      } else if (method === 'POST') {
        response = await this.http.post<T>(url, body, { params }).toPromise();
      } else if (method === 'PUT') {
        response = await this.http.put<T>(url, body, { params }).toPromise();
      } else if (method === 'DELETE') {
        response = await this.http.delete<T>(url, { params }).toPromise();
      } else {
        throw new Error(`Unsupported HTTP method: ${method}`);
      }

      // Show success toast if requested
      if (options.showSuccessToast) {
        this.toast('success', options.successTitle || 'Success', options.successMessage || 'Operation completed');
      }

      return response;
    } catch (error) {
      const action = options.actionDescription || 'perform this operation';
      if (options.silent) console.error(`Failed to ${action}:`, error);
      else this.handleError(error, action);
      return null;
    }
  }

  /**
   * Handle API errors with appropriate user feedback. Single surfacer:
   * the backend always sends its most specific honest message in
   * error.error.error — show it verbatim; only network-down and a total
   * absence of a message get generic text.
   */
  private handleError(error: any, action: string): void {
    console.error(`Failed to ${action}:`, error);

    if (this.isAuthError(error)) {
      this.showAuthModal(action);
      return;
    }

    let message: string;
    if (error instanceof HttpErrorResponse && error.status === 0) {
      message = 'Network problem — check your connection and try again.';
    } else if (error instanceof HttpErrorResponse) {
      message = error.error?.error || `Something went wrong (HTTP ${error.status}). Please try again.`;
    } else {
      message = 'Something went wrong. Please try again.';
    }

    this.showErrorToastWithAction('Xero', `Unable to ${action}. ${message}`);
  }

  /**
   * Check if error means the Xero connection itself needs re-authentication.
   * Uses the structured code from the backend so a Firebase-session 401 doesn't
   * incorrectly trigger the Xero reconnect dialog.
   */
  private isAuthError(error: any): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    return error.error?.code === 'XERO_REAUTH_REQUIRED' || error.error?.code === 'XERO_NOT_CONNECTED';
  }

  /**
   * Show auth error toast with reconnect option
   */
  /**
   * Show auth error dialog with reconnect option
   */
  private showAuthModal(action: string): void {
    this.toast('warning', 'Authentication Required', `Your Xero connection needs to be renewed.`, 3000);

    this.dialogService
      .open(XeroReconnectDialogComponent, {
        context: {
          action: action,
        },
        hasBackdrop: true,
        closeOnBackdropClick: true,
        closeOnEsc: true,
      })
      .onClose.subscribe((result) => {
        if (result === 'reconnect') {
          this.xeroAuthService.initiateXeroConnect().catch(() => {
            this.toast('danger', 'Reconnection Failed', 'Unable to start Xero reconnection.');
          });
        }
      });
  }

  /**
   * Show a toast notification
   */
  private toast(status: NbComponentStatus, title: string, message: string, duration: number = 5000) {
    return this.toastrService.show(message, title, {
      status,
      destroyByClick: true,
      duration,
      position: NbGlobalPhysicalPosition.TOP_RIGHT,
    });
  }

  /**
   * Show an error toast with an action button to manage Xero connection
   */
  private showErrorToastWithAction(title: string, message: string) {
    // Create toast with message
    this.toastrService.show(message, title, {
      status: 'danger',
      destroyByClick: true,
      duration: 8000,
      position: NbGlobalPhysicalPosition.TOP_RIGHT,
      toastClass: 'action-toast',
    });

    // Inject custom button into the toast after it renders
    setTimeout(() => {
      // Find the most recent action toast's message div
      const messageDivs = document.querySelectorAll('nb-toast.action-toast .message');
      const messageDiv = messageDivs[messageDivs.length - 1] as HTMLElement;

      if (messageDiv) {
        // Create button wrapper
        const buttonWrapper = document.createElement('div');
        buttonWrapper.className = 'action-toast-button-wrapper';

        const button = document.createElement('button');
        button.className = 'action-toast-custom-btn';
        button.textContent = 'Manage Connection';
        button.onclick = () => {
          window.location.href = '/home/profile/xero';
        };

        buttonWrapper.appendChild(button);

        // Insert button after the message div (as a sibling)
        messageDiv.parentElement?.appendChild(buttonWrapper);
      }
    }, 50);
  }
}
